import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useRole,
  useInteractions,
  useClick,
  autoPlacement
} from '@floating-ui/react'
import classnames from 'classnames'

import { ReactComponent as AngleIcon } from '../../assets/icons/chevron-arrow-down.svg'

import { insertIf } from '../../helpers/common'

import useStyles from './style'

function Dropdown({
  className,
  triggerClassName,
  arrowClassName,
  showArrowIndicator = false,
  isDisabled,
  triggerElement,
  isOpen,
  onOpenChange,
  placement = 'bottom',
  allowPlacementFlip = true,
  offsetSize = 0,
  closeOnInsideClick = false,
  isFloatingDropdown = true,
  allowedPlacements,
  children
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const classes = useStyles({ dropdownOpen, isDisabled })

  const onVisibilityChange = useCallback(
    isOpen => {
      if (!isDisabled && dropdownOpen !== isOpen) {
        setDropdownOpen(isOpen)
        onOpenChange && onOpenChange(isOpen)
      }
    },
    [dropdownOpen, onOpenChange, isDisabled]
  )

  const handleInsideClick = useCallback(() => {
    closeOnInsideClick && onVisibilityChange(false)
  }, [closeOnInsideClick, onVisibilityChange])

  const { refs, floatingStyles, context } = useFloating({
    open: dropdownOpen,
    onOpenChange: onVisibilityChange,
    whileElementsMounted: autoUpdate,
    placement,
    // the fixes strategy is used because of the issue with wrong positioning in Step forms
    // also cutting the dropdown which go outside relative overflow container
    // also expanding the header width on mobile and horizontal scroll appearing based on that
    strategy: 'fixed',
    middleware: [
      ...insertIf(allowPlacementFlip, flip({ fallbackAxisSideDirection: 'start' })),
      offset(offsetSize),
      shift(),
      ...insertIf(allowedPlacements, autoPlacement({ allowedPlacements }))
    ]
  })

  const click = useClick(context)
  // adds the ability to dismiss the tooltip when the user presses the esc key or click outside.
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'menu' })
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

  const floatingDropdownProps = useMemo(() => {
    return {
      ...getFloatingProps(),
      style: floatingStyles
    }
  }, [getFloatingProps, floatingStyles])

  useEffect(() => {
    // handle manual visibility change from the Parent component
    setDropdownOpen(!!isOpen)
  }, [isOpen])

  return (
    <div className="dropdown">
      <div
        className={classnames(triggerClassName, classes.triggerElement)}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {triggerElement}
        {showArrowIndicator && <AngleIcon className={classnames(arrowClassName, classes.arrowIndicator)} />}
      </div>
      {/* dropdown is not rendered until it will be opened to avoid re-rendering on scroll */}
      {dropdownOpen && (
        <div
          ref={refs.setFloating}
          onClick={handleInsideClick}
          {...(isFloatingDropdown ? { ...floatingDropdownProps } : {})}
          className={classnames(className, classes.dropdownBody)}
        >
          {children}
        </div>
      )}
    </div>
  )
}

export default Dropdown

Dropdown.propTypes = {
  className: PropTypes.string,
  triggerClassName: PropTypes.string,
  arrowClassName: PropTypes.string,
  placement: PropTypes.string,
  allowPlacementFlip: PropTypes.bool,
  offset: PropTypes.array,
  // allow to close Dropdown on the options/body click
  closeOnInsideClick: PropTypes.bool,
  showArrowIndicator: PropTypes.bool,
  isDisabled: PropTypes.bool,
  triggerElement: PropTypes.node,
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  isFloatingDropdown: PropTypes.bool,
  allowedPlacements: PropTypes.array,
  offsetSize: PropTypes.number
}
